import { useEffect, useState } from 'react';

interface PulseEffectProps {
  targetSelector: string;
  color?: string;
}

interface ElementRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * A shine sweep effect that draws attention to the target element.
 * A light sweeps across the element once every few seconds.
 */
export function PulseEffect({
  targetSelector,
  color = '#1976d2',
}: Readonly<PulseEffectProps>) {
  const [rect, setRect] = useState<ElementRect | null>(null);

  useEffect(() => {
    const updateRect = () => {
      const element = document.querySelector(targetSelector);
      if (element) {
        const boundingRect = element.getBoundingClientRect();
        setRect({
          top: boundingRect.top,
          left: boundingRect.left,
          width: boundingRect.width,
          height: boundingRect.height,
        });
      } else {
        setRect(null);
      }
    };

    updateRect();

    // Update on resize/scroll
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [targetSelector]);

  // Check for reduced motion preference
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!rect || prefersReducedMotion) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: rect.top - 4,
        left: rect.left - 4,
        width: rect.width + 8,
        height: rect.height + 8,
        pointerEvents: 'none',
        zIndex: 9998,
        borderRadius: 8,
        border: `2px solid ${color}`,
        overflow: 'hidden',
      }}
    >
      {/* Shine sweep overlay */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `linear-gradient(
            120deg,
            transparent 0%,
            transparent 40%,
            rgba(255, 255, 255, 0.4) 50%,
            transparent 60%,
            transparent 100%
          )`,
          backgroundSize: '200% 100%',
          animation: 'showme-shine-sweep 2.5s ease-in-out infinite',
        }}
      />
      <style>
        {`
          @keyframes showme-shine-sweep {
            0% {
              background-position: 200% 0;
            }
            50% {
              background-position: -200% 0;
            }
            100% {
              background-position: -200% 0;
            }
          }
        `}
      </style>
    </div>
  );
}
